import { CloseCircleFilled, CloseOutlined } from '@ant-design/icons';
import { useTenantTheme } from '@service/tenant/TenantThemeContext';
import {
  Button,
  DatePicker,
  Layout,
  Radio,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { InputTag } from 'src/app/framework/atoms/InputTag/InputTag.atom';
import { CouponDigital } from 'src/models';

import { TFormData } from '../../digital/types/formDataType';
import { useProductDigitalCoupon } from '../../hooks/useProductDigitalCoupon';
import { FAQTooltip } from '../FAQTooltip/FAQTooltip';
import { CouponFormSection } from './CouponFormSection.component';
import {
  Container,
  ErrorContent,
  FormCoupon,
  InputFormCoupon,
  RadioInput,
  RadioInputContainer,
  SidePanelContainer,
  SwitchInputContainer,
  TextAreaFormCoupon,
} from './styles';

type TAddCouponForm = {
  readonly?: boolean;
  isEditing?: boolean;
  coupon?: CouponDigital.IDigitalCoupon;
  couponHookData: ReturnType<typeof useProductDigitalCoupon>;
};

type TLimitDiscount = 'unlimited' | 'limited';

const LENGTH_MIN_CODE = 2;
const LENGTH_MAX_CODE = 20;

export function DigitalCouponForm({
  coupon,
  readonly = false,
  isEditing = false,
  couponHookData,
}: TAddCouponForm) {
  const {
    INFINITE_DISCOUNT_LIMIT,
    productOptions,
    isLoadingProductOptions,
    submitCreateCoupon,
    submitEditCoupon,
    handleDisplayError,
    formRef,
    onSaveCoupon,
    setIsLoading,
    getInvalidOfferIds,
  } = couponHookData;

  const { theme } = useTenantTheme();

  const [form] = FormCoupon.useForm();
  const [active, setActive] = useState<boolean>(true);
  const [limitDiscount, setLimitDiscount] =
    useState<TLimitDiscount>('unlimited');
  const [couponLimitation, setCouponLimitation] =
    useState<CouponDigital.LimitationTypeEnum>(
      CouponDigital.LimitationTypeEnum.UNLIMITED
    );
  const [typeDiscount, setTypeDiscount] = useState<CouponDigital.TypeEnum>(
    CouponDigital.TypeEnum.FIXED
  );
  const [onlyFirstCycle, setOnlyFirstCycle] = useState<boolean>(false);
  const [errorsDisplay, setErrorsDisplay] = useState<string[]>([]);

  const initialValues = useMemo(() => {
    if (!coupon)
      return {
        typeDiscount: CouponDigital.TypeEnum.FIXED,
        purchaseTypeAllowed: CouponDigital.PurchaseTypeAllowedEnum.ALL,
        onlyFirstCycle: false,
        active: true,
      };

    return {
      ...coupon,
      allowedProducts:
        coupon.allowedProducts && coupon.allowedProducts.length > 0
          ? coupon.allowedProducts
          : undefined,
      allowedOffers:
        coupon.allowedOffers && coupon.allowedOffers.length > 0
          ? coupon.allowedOffers.map(offer => String(offer))
          : undefined,
      value: coupon.discount.value,
      typeDiscount: coupon.discount.type,
      startDate: dayjs(coupon.startDate),
      endDate: dayjs(coupon.endDate),
      limitValue:
        coupon.maxUse < INFINITE_DISCOUNT_LIMIT ? coupon.maxUse : undefined,
      purchaseTypeAllowed:
        coupon.purchaseTypeAllowed || CouponDigital.PurchaseTypeAllowedEnum.ALL,
      onlyFirstCycle: coupon.onlyFirstCycle || false,
    };
  }, [coupon]);

  useEffect(() => {
    form.resetFields();
    if (coupon) {
      setTypeDiscount(coupon.discount.type);
      setLimitDiscount(
        coupon.maxUse < INFINITE_DISCOUNT_LIMIT ? 'limited' : 'unlimited'
      );
      if (coupon.allowedOffers && coupon.allowedOffers.length > 0) {
        setCouponLimitation(CouponDigital.LimitationTypeEnum.OFFERS);
      }
      if (coupon.allowedProducts && coupon.allowedProducts.length > 0) {
        setCouponLimitation(CouponDigital.LimitationTypeEnum.PRODUCTS);
      }
      if (
        (!coupon.allowedProducts || coupon.allowedProducts.length === 0) &&
        (!coupon.allowedOffers || coupon.allowedOffers.length === 0)
      ) {
        setCouponLimitation(CouponDigital.LimitationTypeEnum.UNLIMITED);
      }
      setOnlyFirstCycle(coupon?.onlyFirstCycle ?? false);
      setActive(coupon?.active ?? true);
    }
  }, [coupon]);

  const handleSubmit = async (data: unknown) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    try {
      setIsLoading(true);
      const offerIds = form.getFieldValue('allowedOffers')?.map(Number);
      if (offerIds && offerIds.length > 0) {
        const invalidOfferIds = await getInvalidOfferIds(offerIds);
        if (invalidOfferIds && invalidOfferIds.length > 0) {
          setErrorsDisplay([
            `Os ids de oferta: ${invalidOfferIds.join(', ')} são inválidos. Por favor, verifique e tente novamente.`,
          ]);
          return;
        }
        data = {
          ...(data as TFormData),
          allowedOffers: offerIds,
        };
      }
      if (isEditing && coupon) {
        await submitEditCoupon(coupon.id, data as TFormData);
      } else {
        await submitCreateCoupon(data as TFormData);
      }
      !!onSaveCoupon && onSaveCoupon();
    } catch (err) {
      setErrorsDisplay([handleDisplayError(err)]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(
    () => setIsLoading(isLoadingProductOptions),
    [isLoadingProductOptions]
  );

  const handleAddOffer = (values: string[]) => {
    form.setFieldValue('allowedOffers', values);
  };

  return (
    <>
      {errorsDisplay.length > 0 && (
        <ErrorContent>
          <CloseCircleFilled />
          <div>
            <p>
              Há {errorsDisplay.length} erro{errorsDisplay.length !== 1 && 's'}{' '}
              com este cupom:
            </p>

            {errorsDisplay.map(errorMsg => (
              <span key={errorMsg}>-{errorMsg}</span>
            ))}
          </div>
          <Button
            style={{ marginLeft: 'auto', border: 'none' }}
            onClick={() => setErrorsDisplay([])}
          >
            <CloseOutlined />
          </Button>
        </ErrorContent>
      )}
      <Container style={{ overflow: 'hidden' }}>
        <Layout.Content style={{ backgroundColor: 'transparent' }}>
          <FormCoupon
            form={form}
            disabled={readonly}
            layout='vertical'
            ref={formRef as any}
            onFinish={handleSubmit}
            initialValues={initialValues}
            onFinishFailed={({ errorFields }) => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
              setErrorsDisplay(
                errorFields.map(({ errors }) => errors.join(', '))
              );
            }}
          >
            <CouponFormSection title='Dados do Cupom'>
              <FormCoupon.Item
                name='code'
                label='Código do Cupom'
                required
                tooltip='O código deve ser único'
                rules={[
                  { required: true },
                  {
                    pattern: /^[^\s]*$/,
                    message: 'O código não pode ter espaço, utilize "_"',
                  },
                  {
                    min: LENGTH_MIN_CODE,
                    message: `O código deve ter no mínimo ${LENGTH_MIN_CODE} caracteres.`,
                  },
                  {
                    max: LENGTH_MAX_CODE,
                    message: `O código deve ter no máximo ${LENGTH_MAX_CODE} caracteres.`,
                  },
                ]}
              >
                <InputFormCoupon
                  disabled={readonly || isEditing}
                  style={{ textTransform: 'uppercase' }}
                />
              </FormCoupon.Item>
              <FormCoupon.Item
                name='description'
                label='Descrição do Cupom'
                required
                tooltip='Explique o objetivo do cupom'
                rules={[{ required: true }]}
              >
                <TextAreaFormCoupon placeholder='Detalhe o cupom.' />
              </FormCoupon.Item>
            </CouponFormSection>

            <CouponFormSection title='Período' direction='row'>
              <FormCoupon.Item
                name='startDate'
                label='Data de Início'
                required
                style={{ flex: 1 }}
                rules={[{ required: true }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format='DD/MM/YYYY HH:mm'
                  style={{
                    width: '100%',
                    background: 'rgba(20, 20, 20, 1)',
                    border: '1px solid rgba(66, 66, 66, 1)',
                    borderRadius: 6,
                  }}
                />
              </FormCoupon.Item>
              <FormCoupon.Item
                label='Data de Expiração'
                required
                style={{ flex: 1 }}
                name='endDate'
                rules={[{ required: true }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format='DD/MM/YYYY HH:mm'
                  style={{
                    width: '100%',
                    background: 'rgba(20, 20, 20, 1)',
                    border: '1px solid rgba(66, 66, 66, 1)',
                    borderRadius: 6,
                  }}
                />
              </FormCoupon.Item>
            </CouponFormSection>

            <CouponFormSection title='Tipo de desconto (Escolher apenas 1)'>
              <FormCoupon.Item name='typeDiscount' rules={[{ required: true }]}>
                <Radio.Group
                  onChange={e => {
                    form.resetFields(['value']);
                    setTypeDiscount(e.target.value);
                  }}
                >
                  <RadioInputContainer>
                    <RadioInput value={CouponDigital.TypeEnum.FIXED}>
                      Valor Fixo de desconto{' '}
                      <FAQTooltip message='Use virgula para separar os centavos (Não use ponto)' />
                    </RadioInput>
                    <FormCoupon.Item
                      name={
                        typeDiscount === CouponDigital.TypeEnum.FIXED
                          ? 'value'
                          : undefined
                      }
                      rules={[
                        {
                          required:
                            typeDiscount === CouponDigital.TypeEnum.FIXED,
                          message: 'Por favor, insira o valor do cupom',
                        },
                      ]}
                    >
                      <InputFormCoupon
                        required={typeDiscount === CouponDigital.TypeEnum.FIXED}
                        prefix='R$'
                        placeholder='0,00'
                        type='number'
                        disabled={
                          readonly ||
                          typeDiscount !== CouponDigital.TypeEnum.FIXED
                        }
                      />
                    </FormCoupon.Item>
                  </RadioInputContainer>
                  <RadioInputContainer>
                    <RadioInput value={CouponDigital.TypeEnum.PERCENT}>
                      Percentual de desconto{' '}
                      <FAQTooltip message='Use apenas números inteiros' />
                    </RadioInput>
                    <FormCoupon.Item
                      name={
                        typeDiscount === CouponDigital.TypeEnum.PERCENT
                          ? 'value'
                          : undefined
                      }
                      rules={[
                        {
                          required:
                            typeDiscount === CouponDigital.TypeEnum.PERCENT,
                          message: 'Por favor, insira a porcentagem desejada',
                        },
                        {
                          pattern: /^-?\d+$/,
                          message: 'Por favor, insira um número inteiro',
                        },
                      ]}
                    >
                      <InputFormCoupon
                        required={
                          typeDiscount === CouponDigital.TypeEnum.PERCENT
                        }
                        suffix='%'
                        placeholder='0'
                        type='number'
                        disabled={
                          readonly ||
                          typeDiscount !== CouponDigital.TypeEnum.PERCENT
                        }
                      />
                    </FormCoupon.Item>
                  </RadioInputContainer>
                </Radio.Group>
              </FormCoupon.Item>
            </CouponFormSection>

            <CouponFormSection title='Limites de uso (Escolher apenas 1)'>
              <Radio.Group
                defaultValue='unlimited'
                value={limitDiscount}
                onChange={e => {
                  form.resetFields(['limitValue']);
                  setLimitDiscount(e.target.value);
                }}
              >
                <RadioInputContainer>
                  <RadioInput value='unlimited'>
                    Sem limite geral de uso
                  </RadioInput>
                </RadioInputContainer>
                <RadioInputContainer>
                  <RadioInput value='limited'>
                    Limite de uso geral{' '}
                    <FAQTooltip message='Use apenas números inteiros' />
                  </RadioInput>

                  <FormCoupon.Item
                    style={{ maxWidth: '280px' }}
                    name={
                      limitDiscount === 'unlimited' ? undefined : 'limitValue'
                    }
                    rules={[
                      {
                        required: limitDiscount === 'limited',
                        message: 'Por favor, insira o limite Geral',
                      },
                      {
                        pattern: /^-?\d+$/,
                        message: 'Por favor, insira um número inteiro',
                      },
                    ]}
                  >
                    <InputFormCoupon
                      suffix='unid.'
                      placeholder='0'
                      type='number'
                      disabled={readonly || limitDiscount !== 'limited'}
                    />
                  </FormCoupon.Item>
                </RadioInputContainer>
              </Radio.Group>
            </CouponFormSection>

            <CouponFormSection title='Condições de uso'>
              <FormCoupon.Item
                required
                style={{ flex: 1 }}
                name='purchaseTypeAllowed'
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio value={CouponDigital.PurchaseTypeAllowedEnum.ALL}>
                    Sem restrição
                  </Radio>
                  <Radio value={CouponDigital.PurchaseTypeAllowedEnum.FIRST}>
                    Apenas na primeira compra
                  </Radio>
                  <Radio
                    value={CouponDigital.PurchaseTypeAllowedEnum.RECURRENT}
                  >
                    Apenas para Ex-Clientes
                  </Radio>
                </Radio.Group>
              </FormCoupon.Item>
            </CouponFormSection>

            <CouponFormSection title='Ciclo de aplicação do cupom'>
              <FormCoupon.Item name='onlyFirstCycle'>
                <SwitchInputContainer>
                  <span>Desativado na renovação</span>
                  <FAQTooltip message='Quando ativado, o cupom não será considerado no valor de renovação.' />
                  <Switch
                    checked={onlyFirstCycle}
                    disabled={readonly || isEditing}
                    onChange={value => {
                      setOnlyFirstCycle(value);
                      form.setFieldValue('onlyFirstCycle', value);
                    }}
                  ></Switch>
                </SwitchInputContainer>
              </FormCoupon.Item>
            </CouponFormSection>

            <CouponFormSection title='Configuração de disponibilidade'>
              <RadioInputContainer>
                <Radio.Group
                  defaultValue={CouponDigital.LimitationTypeEnum.UNLIMITED}
                  value={couponLimitation}
                  onChange={e => {
                    setCouponLimitation(e.target.value);
                    form.setFieldValue('allowedProducts', undefined);
                    form.setFieldValue('allowedOffers', undefined);
                  }}
                >
                  <Space direction='horizontal'>
                    <RadioInput
                      value={CouponDigital.LimitationTypeEnum.UNLIMITED}
                    >
                      Todos
                    </RadioInput>
                    <RadioInput
                      value={CouponDigital.LimitationTypeEnum.PRODUCTS}
                    >
                      Produtos determinados
                    </RadioInput>
                    <RadioInput value={CouponDigital.LimitationTypeEnum.OFFERS}>
                      Ofertas determinadas
                    </RadioInput>
                  </Space>
                </Radio.Group>
                <div style={{ minHeight: '56px', paddingBlockStart: '16px' }}>
                  <span
                    style={{
                      width: '100%',
                      flex: 1,
                      color: '#ACACAC',
                      display:
                        couponLimitation ===
                        CouponDigital.LimitationTypeEnum.UNLIMITED
                          ? 'inline'
                          : 'none',
                    }}
                  >
                    Cupom será aplicável a todos os produtos e ofertas
                  </span>
                  <FormCoupon.Item
                    required
                    style={{
                      flex: 1,
                      display:
                        couponLimitation ===
                        CouponDigital.LimitationTypeEnum.PRODUCTS
                          ? 'block'
                          : 'none',
                    }}
                    name='allowedProducts'
                    rules={[
                      {
                        required:
                          couponLimitation ===
                          CouponDigital.LimitationTypeEnum.PRODUCTS,
                        message: 'Por favor, selecione ao menos um produto',
                      },
                    ]}
                  >
                    <Select
                      mode='multiple'
                      disabled={readonly}
                      options={productOptions}
                    />
                  </FormCoupon.Item>
                  <FormCoupon.Item
                    required
                    style={{
                      flex: 1,
                      display:
                        couponLimitation ===
                        CouponDigital.LimitationTypeEnum.OFFERS
                          ? 'block'
                          : 'none',
                    }}
                    name='allowedOffers'
                    rules={[
                      {
                        required:
                          couponLimitation ===
                          CouponDigital.LimitationTypeEnum.OFFERS,
                        message: 'Por favor, selecione ao menos uma oferta',
                      },
                    ]}
                  >
                    <InputTag
                      placeHolder='Insira o id da oferta e pressione enter'
                      onChange={value => handleAddOffer(value)}
                      type='number'
                      defaultValue={form.getFieldValue('allowedOffers')}
                    />
                  </FormCoupon.Item>
                </div>
              </RadioInputContainer>
            </CouponFormSection>
            <CouponFormSection title='Status do cupom'>
              <FormCoupon.Item name='active'>
                <SwitchInputContainer>
                  <span>inativo</span>
                  <Switch
                    checked={active}
                    onChange={value => {
                      setActive(value);
                      form.setFieldValue('active', value);
                    }}
                  ></Switch>
                  <span>ativo</span>
                </SwitchInputContainer>
              </FormCoupon.Item>
            </CouponFormSection>
          </FormCoupon>
        </Layout.Content>
        <SidePanelContainer offsetTop={20}>
          <Layout.Sider
            style={{
              backgroundColor: 'transparent',
              marginLeft: 16,
              flex: 1,
            }}
            width={480}
          >
            <CouponFormSection title='Regras cupom Digital'>
              <Typography
                style={{
                  color: 'rgba(255, 255, 255, 0.65)',
                }}
              >
                <b>Código do cupom:</b>
                {` `}É o cupom em si, deve ser cadastrado com letra maiúscula.
                Mínimo 2 caracteres e sem espaço;
                <br />
                <br />
                <b>Descrição:</b>
                {` `}Campo de uso interno ${theme?.extras?.tenantName}
                <br />
                <br />
                <b>Percentual de desconto e Valor fixo:</b> Campo numérico que
                representar a opção escolhida. <br />
                <small>
                  Exemplo: Para 10%OFF, digitar 10. Para R$100 OFF, digitar 100.
                </small>
                <br />
                <br />
                <b>Limite de uso geral:</b>
                {` `}
                Definir o máximo de vezes que o cupom pode ser utilizado,
                independente do cliente.
              </Typography>
            </CouponFormSection>
          </Layout.Sider>
        </SidePanelContainer>
      </Container>
    </>
  );
}
